import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useForm, useController } from 'react-hook-form';
import {
  formatEuro,
  getTauxTVA,
  calculateChargesTotal,
  calculateFraisProfessionnels,
  validateCA,
} from '../utils/helpers';
import { DEFAULTS_CHARGES_HORS_CAE, MAX_CHARGES } from '../utils/constants';

// Liste des métiers
const METIERS = [
  "Commerce (achat, revente)",
  "Bâtiment, travaux",
  "Création artisanale",
  "Production alimentaire, culinaire",
  "Prestation de services (conseil, bureau d'études, communication, etc.)",
  "Informatique, numérique",
  "Formation professionnelle",
  "Culturelle, artistique",
  "Agriculture, paysage",
  "Service à la personne",
  "Bien-être, soin"
];

// Définition des charges professionnelles groupées
// has_default_hors_cae : valeur par défaut appliquée dans les calculs non-CAE si le champ est vide
//   (la valeur réelle vient de config.defaults_charges_hors_cae ou de MAX_CHARGES/DEFAULTS_CHARGES_HORS_CAE)
// has_max : plafond mensuel (valeur réelle depuis config.max_charges ou MAX_CHARGES)
// masque_si : masque le champ pour les métiers listés (et exclut la valeur des calculs)
const CHARGES_PROFESSIONNELLES = [
  {
    categorie: "Charges directes",
    charges: [
      {
        id: "matieres_premieres",
        label: "Matières premières et marchandises vendues",
        tooltip: "La matière première est transformée pour vendre un produit. Les marchandises sont revendues en l'état. Pour le métier du bâtiment, si aucune valeur n'est saisie, un montant égal à 45% du CA est appliqué.",
        placeholder: "Ex: 2000",
      },
      {
        id: "sous_traitance",
        label: "Achat de sous-traitance utilisée directement liée à votre l'activité",
        tooltip: "Montant facturé par des prestataires externes pour réaliser une partie de votre activité.",
        placeholder: "Ex: 500",
      },
    ],
  },
  {
    categorie: "Frais généraux",
    charges: [
      {
        id: "loyer",
        label: "Loyer & assurance loyer des locaux",
        tooltip: "Si bail ou mise à disposition d'un local pour les besoins professionnels : montant du loyer au prorata de l'espace réellement occupé et du prix du marché.",
        placeholder: "Ex: 300",
      },
      {
        id: "internet_telephone",
        label: "Abonnement internet et téléphone",
        tooltip: "Seuls 50% du montant saisi est pris en compte.",
        placeholder: "Ex: 40",
      },
    ],
  },
  {
    categorie: "Déplacements et frais de mission",
    charges: [
      {
        id: "indemnites_km",
        label: "Indemnités kilométriques (déplacements professionnels)",
        tooltip: "Frais kilométriques calculés sur le barème fiscal. Un plafond mensuel s'applique.",
        placeholder: "Ex: 150",
        has_max: true,
        link: { href: "https://www.impots.gouv.fr/simulateur-bareme-kilometrique", label: "Calculer mes indemnités km" },
      },
      {
        id: "repas_affaires",
        label: "Repas d'affaires",
        tooltip: "Plafonné à 20 fois l'indemnité journalière en vigueur.",
        placeholder: "Ex: 84",
        has_max: true,
      },
      {
        id: "deplacements",
        label: "Déplacements ponctuels",
        tooltip: "Frais de déplacements professionnels (autoroute, train, hôtel).",
        placeholder: "Ex: 100",
      },
    ],
  },
  {
    categorie: "Communication et outils",
    charges: [
      {
        id: "communication_pub",
        label: "Communication, publicité",
        tooltip: "Frais de communication et de publicité (cartes de visite, site web, etc.).",
        placeholder: "Ex: 100",
      },
      {
        id: "consommables",
        label: "Consommables, énergies, études, documentation, équipements de protection individuelle (EPI)",
        tooltip: "Fournitures de bureau et équipements de protection individuelle.",
        placeholder: "Ex: 30",
      },
    ],
  },
  {
    categorie: "Autres frais de l'activité (mutualisés en CAE)",
    charges: [
      {
        id: "label_bio",
        label: "Label BIO",
        tooltip: "Coût de certification biologique.",
        placeholder: "Ex: 50",
        visible_si: ["Production alimentaire, culinaire"],
      },
      {
        id: "logiciel_formation",
        label: "Logiciel formation professionnelle",
        tooltip: "Logiciel spécifique au secteur de la formation.",
        placeholder: "Ex: 100",
        visible_si: ["Formation professionnelle"],
        has_default_hors_cae: true,
      },
      {
        id: "logiciel_compta",
        label: "Logiciel de comptabilité",
        tooltip: "Logiciel de comptabilité inclus dans les services de la CAE. Une valeur par défaut est appliquée dans les comparaisons hors CAE si ce champ est vide.",
        placeholder: "Ex: 50",
        has_default_hors_cae: true,
      },
      {
        id: "compte_bancaire",
        label: "Compte bancaire",
        tooltip: "Frais mensuels du compte bancaire professionnel ou classique(micro). Une valeur par défaut est appliquée dans les comparaisons hors CAE si ce champ est vide.",
        placeholder: "Ex: 20",
        has_default_hors_cae: true,
        tva_exempt: true,
      },
      {
        id: "assurance_rcp",
        label: "Assurance responsabilité civile professionnelle",
        tooltip: "Assurance RCP. Une valeur par défaut est appliquée dans les comparaisons hors CAE si ce champ est vide.",
        placeholder: "Ex: 15",
        has_default_hors_cae: true,
        masque_si: ["Bâtiment, travaux"],
        tva_exempt: true,
      },
      {
        id: "assurance_decennale",
        label: "Assurance décennale",
        tooltip: "Obligatoire pour les métiers du bâtiment. Une valeur par défaut est appliquée dans les comparaisons hors CAE si ce champ est vide.",
        placeholder: "Ex: 250",
        has_default_hors_cae: true,
        visible_si: ["Bâtiment, travaux"],
        tva_exempt: true,
      },
      {
        id: "comptable",
        label: "Comptable",
        tooltip: "Honoraires comptables mensuels. Une valeur par défaut est appliquée dans les comparaisons hors CAE si ce champ est vide.",
        placeholder: "Ex: 250",
        has_default_hors_cae: true,
      },
      {
        id: "cet_cvae_cfe",
        label: "CET : CVAE et CFE",
        tooltip: "Contribution économique territoriale (CVAE + CFE). Une valeur par défaut est appliquée dans les comparaisons hors CAE si ce champ est vide.",
        placeholder: "Ex: 58",
        has_default_hors_cae: true,
        tva_exempt: true,
      },
      {
        id: "mutuelle_prevoyance",
        label: "Mutuelle et prévoyance",
        tooltip: "Cotisation mensuelle mutuelle et prévoyance. Une valeur par défaut est appliquée dans les comparaisons hors CAE si ce champ est vide.",
        placeholder: "Ex: 90",
        has_default_hors_cae: true,
        tva_exempt: true,
      },
    ],
  },
];

// Composant Tooltip (text: string simple | content: JSX riche)
function Tooltip({ text, content, children }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, openUp: false });
  const ref = useRef(null);

  const handleMouseEnter = () => {
    if (ref.current) {
      const elRect = ref.current.getBoundingClientRect();
      const container = ref.current.closest('form') ?? ref.current.parentElement;
      const containerRect = container.getBoundingClientRect();
      const openUp = elRect.top - containerRect.top > containerRect.height / 2;
      const MARGIN = 8;
      const tooltipWidth = Math.min(window.innerWidth * 0.9, 768);
      const left = Math.max(MARGIN, Math.min(elRect.left, window.innerWidth - tooltipWidth - MARGIN));
      setPos({
        top: openUp ? elRect.top - 6 : elRect.bottom + 6,
        left,
        openUp,
      });
    }
    setShow(true);
  };

  return (
    <span
      ref={ref}
      className="relative inline-flex items-center gap-1.5 cursor-default"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShow(!show)}
        className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-primary-600 hover:text-primary-700 border border-primary-300 rounded-full hover:bg-primary-50 transition-colors flex-shrink-0"
        aria-label="Aide"
      >
        ⓘ
      </button>
      {show && createPortal(
        <div
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            transform: pos.openUp ? 'translateY(-100%)' : 'none',
            zIndex: 9999,
            maxWidth: '90vw',
            width: '48rem',
          }}
          className="p-3 text-sm text-white bg-gray-900 rounded-lg shadow-lg animate-fade-in"
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          <div className={`absolute w-2 h-2 bg-gray-900 transform rotate-45 left-3 ${pos.openUp ? '-bottom-1' : '-top-1'}`} />
          {content ?? text}
        </div>,
        document.body
      )}
    </span>
  );
}

Tooltip.propTypes = {
  text: PropTypes.string,
  content: PropTypes.node,
  children: PropTypes.node,
};

// Formate un nombre en style français (espace pour les milliers)
function formatNombre(val) {
  const num = Number(val);
  if (!val || isNaN(num) || num === 0) return '';
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(num);
}

// Input numérique : saisie libre au focus, formaté au blur
function NumericInput({ control, name, rules, className, placeholder, id }) {
  const { field } = useController({ control, name, rules, defaultValue: 0 });
  const [isFocused, setIsFocused] = useState(false);
  const [rawInput, setRawInput] = useState('');

  const handleFocus = () => {
    setIsFocused(true);
    setRawInput(field.value ? String(field.value) : '');
  };

  const handleChange = (e) => setRawInput(e.target.value);

  const handleBlur = () => {
    setIsFocused(false);
    const cleaned = rawInput.replace(/[\s\u00a0]/g, '').replace(',', '.');
    const num = parseFloat(cleaned);
    field.onChange(isNaN(num) || num < 0 ? 0 : num);
    field.onBlur();
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      id={id || name}
      ref={field.ref}
      name={field.name}
      value={isFocused ? rawInput : formatNombre(field.value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={className}
      placeholder={placeholder}
    />
  );
}

NumericInput.propTypes = {
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  rules: PropTypes.object,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  id: PropTypes.string,
};

function Step1FormSaisie({ data, updateData, onNext }) {
  const { control, register, handleSubmit, watch, formState: { errors } } = useForm({
    mode: 'onBlur',
    defaultValues: {
      metier: data.metier || '',
      ca_montant: data.caMontant || data.caHT || '',
      ca_type: data.isTTC ? 'ttc' : 'ht',

      ...Object.keys(data.chargesProfessionnelles || {}).reduce((acc, key) => {
        acc[key] = data.chargesProfessionnelles[key] || 0;
        return acc;
      }, {}),
    },
  });

  const [config, setConfig] = useState(null);

  // Charger la configuration CAE
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await fetch('/config/config.json');
        const configData = await response.json();
        setConfig(configData);
      } catch (error) {
        console.error('Erreur chargement configuration:', error);
      }
    };
    loadConfig();
  }, []);

  // Watch les valeurs du formulaire
  const metier = watch('metier');
  const caMontant = watch('ca_montant');
  const caType = watch('ca_type');
  const tvaTaux = getTauxTVA(metier, config?.metiers);

  // Calculer CA HT et TTC
  const montant = Number(caMontant) || 0;
  const caHT = caType === 'ht' ? montant : montant / (1 + tvaTaux);
  const caTTC = caType === 'ttc' ? montant : montant * (1 + tvaTaux);

  // Résoudre les valeurs de config (avec fallback sur les constantes)
  const defaults = { ...DEFAULTS_CHARGES_HORS_CAE, ...config?.defaults_charges_hors_cae };
  const maxCharges = { ...MAX_CHARGES, ...config?.max_charges };

  // Vérifier si une charge doit être affichée
  const shouldShowCharge = (charge) => {
    if (charge.masque_si?.includes(metier)) return false;
    if (!charge.visible_si) return true;
    return charge.visible_si.includes(metier);
  };

  // Calculer le total des charges en temps réel
  const allChargesDefs = CHARGES_PROFESSIONNELLES.flatMap(cat => cat.charges);
  const chargesValues = watch(allChargesDefs.map(c => c.id));

  // Charges réelles (CAE) : valeur saisie ou 0 ; 0 si le champ est masqué
  const chargesObject = allChargesDefs.reduce((acc, charge, index) => {
    const visible = shouldShowCharge(charge);
    const val = visible ? Number(chargesValues[index]) || 0 : 0;
    acc[charge.id] = charge.has_max ? Math.min(val, maxCharges[charge.id] ?? Infinity) : val;
    return acc;
  }, {});

  // Charges hors-CAE : mêmes règles + valeur par défaut pour les champs vides qui ont has_default_hors_cae
  const chargesObjectHorsCAE = allChargesDefs.reduce((acc, charge, index) => {
    const visible = shouldShowCharge(charge);
    if (!visible) { acc[charge.id] = 0; return acc; }
    const val = Number(chargesValues[index]) || 0;
    const capped = charge.has_max ? Math.min(val, maxCharges[charge.id] ?? Infinity) : val;
    const minVal = charge.has_default_hors_cae ? (defaults[charge.id] ?? 0) : 0;
    acc[charge.id] = Math.max(capped, minVal);
    return acc;
  }, {});

  const totalCharges = calculateChargesTotal({
    ...chargesObject,
    frais_professionnels_total: calculateFraisProfessionnels(chargesObject),
  });

  // Total Charges = valeurs réellement utilisées dans les calculs hors-CAE
  // (défauts appliqués + 45% CA pour bâtiment si matières premières vides)
  const matieres_effectives =
    chargesObject.matieres_premieres > 0
      ? chargesObject.matieres_premieres
      : metier === 'Bâtiment, travaux' ? caHT * 0.45 : 0;

  const totalChargesEffectif =
    matieres_effectives
    + (chargesObjectHorsCAE.sous_traitance || 0)
    + calculateFraisProfessionnels(chargesObjectHorsCAE);

  // Détail par ligne pour le tooltip du total
  const chargesValuesMap = Object.fromEntries(
    allChargesDefs.map((c, i) => [c.id, Number(chargesValues[i]) || 0])
  );
  const lignesChargesEffectives = allChargesDefs
    .filter(charge => shouldShowCharge(charge))
    .reduce((acc, charge) => {
      const valSaisie = chargesValuesMap[charge.id] || 0;
      let val, note;

      if (charge.id === 'matieres_premieres') {
        if (valSaisie > 0) { val = valSaisie; }
        else if (metier === 'Bâtiment, travaux') { val = caHT * 0.45; note = '45% du CA HT (défaut bâtiment)'; }
        else return acc;
      } else if (charge.id === 'internet_telephone') {
        if (valSaisie <= 0) return acc;
        val = valSaisie * 0.5;
        note = `${valSaisie} € × 50% (part professionnelle)`;
      } else if (valSaisie === 0 && charge.has_default_hors_cae) {
        val = defaults[charge.id] ?? 0;
        if (val === 0) return acc;
        note = 'minimum appliqué (saisie vide)';
      } else if (valSaisie === 0) {
        return acc;
      } else {
        val = charge.has_max ? Math.min(valSaisie, maxCharges[charge.id] ?? Infinity) : valSaisie;
        if (val < valSaisie) note = `plafonné à ${val} €`;
        if (charge.has_default_hors_cae) {
          const minVal = defaults[charge.id] ?? 0;
          if (val < minVal) { val = minVal; note = `minimum ${minVal} € appliqué`; }
        }
      }

      acc.push({ label: charge.label, val: Math.round(val), note: note ?? null });
      return acc;
    }, []);

  const tooltipTotalCharges = (
    <div>
      <p className="font-semibold text-white mb-2 border-b border-gray-600 pb-1">
        Charges retenues pour les calculs
      </p>
      <div className="space-y-1">
        {lignesChargesEffectives.map((ligne, i) => (
          <div key={i} className="flex justify-between gap-6 text-sm">
            <span className="text-gray-300 truncate">{ligne.label}</span>
            <span className="whitespace-nowrap text-right">
              <span className="text-white font-medium">{ligne.val} €</span>
              {ligne.note && <span className="ml-1 text-yellow-300 text-xs">← {ligne.note}</span>}
            </span>
          </div>
        ))}
        {lignesChargesEffectives.length === 0 && (
          <p className="text-gray-400 text-sm italic">Aucune charge saisie ou par défaut.</p>
        )}
      </div>
      <div className="border-t border-gray-500 mt-2 pt-2 flex justify-between font-semibold">
        <span>Total Charges</span>
        <span>{Math.round(totalChargesEffectif)} €</span>
      </div>
      <p className="text-xs text-gray-400 mt-2">
        Certaines de ces charges sont inclues dans les services de la CAE. 
      </p>
    </div>
  );

  // Entrée valide le champ en cours (commit via blur) sans soumettre le formulaire
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName === 'INPUT' && e.target.type === 'text') {
      e.preventDefault();
      e.target.blur();
    }
  };

  // Soumission du formulaire
  const onSubmit = (formData) => {
    // Validation du CA
    const validationCA = validateCA(caHT, 200000);
    if (!validationCA.valid) {
      alert(validationCA.error);
      return;
    }

    // Mettre à jour les données globales
    updateData({
      metier: formData.metier,
      caMontant: Number(caMontant) || 0,
      caHT: caHT,
      caTTC: caTTC,
      isTTC: caType === 'ttc',
      tvaTaux: tvaTaux,
      chargesProfessionnelles: {
        ...chargesObject,
        frais_professionnels_total: calculateFraisProfessionnels(chargesObject),
      },
      chargesProfessionnellesHorsCAE: {
        ...chargesObjectHorsCAE,
        frais_professionnels_total: calculateFraisProfessionnels(chargesObjectHorsCAE),
      },
      totalCharges: totalCharges,
    });

    // Passer à l'étape suivante
    onNext();
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-card p-6 md:p-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            📝 Collecte des informations
          </h2>
          <p className="text-gray-600">
            Renseignez vos données pour calculer votre revenu net selon différents statuts juridiques
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown} className="space-y-8">
          {/* 1. MÉTIER */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-bold text-sm">
                1
              </span>
              Métier exercé ou souhaité
            </h3>

            <div>
              <label htmlFor="metier" className="block text-sm font-medium text-gray-700 mb-2">
                <Tooltip text="Le métier détermine certaines charges spécifiques appliquées dans la simulation : assurance décennale pour le bâtiment, label bio pour l'alimentaire, logiciel métier pour la formation…">
                  Sélectionnez votre métier
                </Tooltip>
                <span className="text-red-500 ml-1" aria-hidden="true">*</span>
              </label>
              <select
                id="metier"
                {...register('metier', { required: 'Le métier est obligatoire' })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">-- Choisissez un métier --</option>
                {METIERS.filter(m => config?.metiers?.[m]?.visible !== false).map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              {errors.metier && (
                <p className="mt-1 text-sm text-red-600">{errors.metier.message}</p>
              )}
            </div>
          </div>

          {/* 2. CHIFFRE D'AFFAIRES */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-bold text-sm">
                2
              </span>
              Chiffre d'affaires moyen mensuel
            </h3>

            <div className="space-y-4">
              {/* Montant CA */}
              <div>
                <label htmlFor="ca_montant" className="block text-sm font-medium text-gray-700 mb-2">
                  <Tooltip text="Indiquez votre chiffre d'affaires mensuel moyen, en HT ou TTC selon ce que vous connaissez le mieux.">
                    Montant
                  </Tooltip>
                  <span className="text-red-500 ml-1" aria-hidden="true">*</span>
                </label>
                <div className="relative">
                  <NumericInput
                    control={control}
                    name="ca_montant"
                    id="ca_montant"
                    rules={{
                      required: 'Le chiffre d\'affaires est obligatoire',
                      min: { value: 0, message: 'Le montant doit être positif' },
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-12"
                    placeholder="Ex: 10000"
                  />
                  <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                    €
                  </span>
                </div>
                {errors.ca_montant && (
                  <p className="mt-1 text-sm text-red-600">{errors.ca_montant.message}</p>
                )}
              </div>

              {/* Radio HT/TTC */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  <Tooltip text="Ce choix s'applique à toutes les valeurs saisies : chiffre d'affaires ET charges professionnelles. Choisissez le type de montant que vous connaissez le mieux — en général, TTC correspond aux montants que vous encaissez et payez au quotidien.">
                    Type de montant — CA et charges
                  </Tooltip>
                </p>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="ht"
                      {...register('ca_type')}
                      className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Hors Taxes (HT)</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="ttc"
                      {...register('ca_type')}
                      className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Toutes Taxes Comprises (TTC)</span>
                  </label>
                </div>
              </div>

              {/* Exonération TVA — dès que le métier est sélectionné */}
              {metier && tvaTaux === 0 && (
                <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800">
                  <span className="mt-0.5 flex-shrink-0">✅</span>
                  <span>
                    Ce secteur peut-être <strong>exonéré de TVA</strong> — Toutefois, dans le cadre de ce simulateur, les montants HT et TTC sont identiques. La distinction HT / TTC ne s'applique pas.
                  </span>
                </div>
              )}

              {/* Taux TVA retenu — uniquement si TTC sélectionné, métier connu et TVA non nulle */}
              {caType === 'ttc' && metier && tvaTaux > 0 && montant > 0 && (
                <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-800">
                  <span className="mt-0.5 flex-shrink-0">ℹ️</span>
                  <span>
                    Taux de TVA retenu pour ce secteur : <strong>{(tvaTaux * 100).toLocaleString('fr-FR')}%</strong>
                    {' '}— montant HT estimé : <strong>{Math.round(caHT).toLocaleString('fr-FR')} €</strong>
                    <br />
                    <span className="text-blue-600 text-xs">Ce taux peut varier selon les produits — il sera affiné lors de votre accompagnement en CAE.</span>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 3. CHARGES PROFESSIONNELLES */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-bold text-sm">
                3
              </span>
              Charges professionnelles mensuelles
            </h3>
            <p className="text-sm text-gray-500 italic">
              Aucun champ n'est obligatoire, mais plus vous renseignerez de valeurs, plus le résultat sera représentatif de votre situation réelle.
            </p>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
              <span className="mt-0.5 flex-shrink-0">ℹ️</span>
              <span>
                Le simulateur applique <strong>20% de TVA</strong> sur les charges saisies en <strong>HT</strong>.
                Les charges marquées <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full border border-green-300">Exonéré TVA</span> en sont <strong>exonérées</strong> (assurances, frais bancaires, taxes).
              </span>
            </div>

            <div className="space-y-5">
              {CHARGES_PROFESSIONNELLES.map((categorie) => {
                const visibleCharges = categorie.charges.filter(shouldShowCharge);
                if (visibleCharges.length === 0) return null;
                return (
                  <div key={categorie.categorie}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 px-1">
                      {categorie.categorie}
                    </p>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      {visibleCharges.map((charge, idx) => (
                        <div key={charge.id}>
                          <div className={`grid grid-cols-[1fr_9rem] gap-3 px-4 py-2.5 items-center ${idx < visibleCharges.length - 1 ? 'border-b border-gray-100' : ''}`}>
                            <label htmlFor={charge.id} className="text-sm font-medium text-gray-700">
                              <Tooltip text={charge.tooltip}>
                                {charge.label}
                              </Tooltip>
                              {charge.tva_exempt && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full border border-green-300">
                                  Exonéré TVA
                                </span>
                              )}
                              {charge.link && (
                                <a
                                  href={charge.link.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-2 text-xs text-primary-600 hover:text-primary-800 hover:underline font-normal"
                                >
                                  ↗ {charge.link.label}
                                </a>
                              )}
                            </label>
                            <div className="relative">
                              <NumericInput
                                control={control}
                                name={charge.id}
                                id={charge.id}
                                rules={charge.has_max ? {
                                  max: {
                                    value: maxCharges[charge.id],
                                    message: `Max : ${maxCharges[charge.id]} €`,
                                  },
                                } : {}}
                                className={`w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-7 text-right ${errors[charge.id] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                                placeholder="0"
                              />
                              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                                €
                              </span>
                            </div>
                            {errors[charge.id] && (
                              <p className="text-xs text-red-500 mt-0.5 text-right">{errors[charge.id].message}</p>
                            )}
                          </div>
                          {charge.note && (
                            <p className="px-4 pb-1.5 text-xs text-gray-400 italic">{charge.note}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total des charges */}
            <div className="bg-primary-50 border-2 border-primary-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-primary-900">
                  <Tooltip content={tooltipTotalCharges}>
                    📊 Total des charges professionnelles retenues pour les calculs
                  </Tooltip>
                </span>
                <span className="text-2xl font-bold text-primary-700">
                  {formatEuro(totalChargesEffectif, 0)}
                </span>
              </div>
            </div>
          </div>

          {/* 5. CHARGES CAE */}
          {/*config && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-bold text-sm">
                  5
                </span>
                Charges CAE
              </h3>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start gap-3 mb-4">
                  <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="font-semibold text-blue-900 mb-2">
                      🏢 Charges CAE (Pré-remplies par votre CAE)
                    </p>
                    <p className="text-sm text-blue-800">
                      Ces montants sont spécifiques à {config.cae_info.nom} et seront appliqués automatiquement.
                    </p>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-blue-800">Contribution services mutualisés :</span>
                    <span className="font-semibold text-blue-900">
                      {config.parametres_cae.contribution_pourcentage}% marge brute
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-800">RCP, CGscop :</span>
                    <span className="font-semibold text-blue-900">
                      {metier === 'Bâtiment, travaux'
                        ? formatEuro(
                            (config.parametres_cae.forfait_rcp_cae_batiment ?? 20)
                            + caHT * (config.parametres_cae.tx_decennale_batiment ?? 0.03),
                            2,
                          )
                        : formatEuro(config.parametres_cae.forfait_rcp_cae_hors_batiment ?? 15, 0)
                      }/mois
                      {metier === 'Bâtiment, travaux' && (
                        <span className="text-xs text-blue-600 ml-1">(forfait + 3% CA)</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-800">Provision intéressement :</span>
                    <span className="font-semibold text-blue-900">
                      {config.parametres_cae.provision_interessement_pourcentage}% marge éco.
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-800">Charges forfaitaires :</span>
                    <span className="font-semibold text-blue-900">
                      {formatEuro((config.parametres_cae.charges_forfaitaires_mutuelles ?? 84) + (config.parametres_cae.charges_forfaitaires_annuel_medecine_travail ?? 95) / 12, 0)}/mois
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-800">Cotisation Fédé/URSCOP :</span>
                    <span className="font-semibold text-blue-900">
                      {formatEuro(config.parametres_cae.cotisation_fede_urscop, 0)}/mois
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )*/}

          {/* BOUTON SUBMIT */}
          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              type="submit"
              className="px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-bold text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-3"
            >
              <span>🧮</span>
              <span>Calculer mon revenu net</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </form>
      </div>

      {/* Note d'aide */}
      <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">
              Besoin d'aide pour remplir le formulaire ?
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              Survolez les titres suivis d'une icône (ⓘ) pour obtenir des explications détaillées sur chaque champ
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

Step1FormSaisie.propTypes = {
  data: PropTypes.object.isRequired,
  updateData: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default Step1FormSaisie;
